import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'

import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors, showNotification } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppRadio from '@/components/AppRadio.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppArcSelector from '@/components/AppArcSelector.vue'
import { getPoliciesReport, getPolicesExpiringInAMonth, getPolicesExpiredLastMonth, exportPolicyReports } from '@/modules/PolicyReports/gql/policy.gql'
import { policyStatus, policyStatusClass, billingType, statusValues, policyTypeKeys, policyTypeValues, policyType } from '@/modules/Policy/helpers/policy'
import ListPage from '@/mixins/list-page'
import SearchCarrier from '@/mixins/search-queries/search-carrier'
import SearchCarrierProduct from '@/mixins/search-queries/search-carrier-product'
import SearchProducer from '@/mixins/search-queries/search-producer'
import SearchGeneralAgent from '@/mixins/search-queries/search-ga'
import SearchBook from '@/mixins/search-queries/search-book'
import RecordTypesHandler from '@/mixins/record-types'
import dollarFilter from '@/utils/dollarFilter'
import ExportExcel from '@/mixins/export-excel'
import { dateToDisplay } from '@/utils/dateTime'
import { SnackType } from '@/interfaces/snack'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import { PageState } from '../../../store/modules/page'
import TableCheckList from '../components/TableCheckList.vue'
import * as R from 'ramda'
import { User } from '@/modules/User/interfaces/user'
import { CheckboxModel } from '../interfaces/checkbox-model'

export interface InfoItem {
  title: string
  value: string
}

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh,
    AppDialog,
    AppRadio,
    AppTextSearch,
    AppCheckbox,
    AppTableDate,
    AppSelector,
    AppListSearchBtn,
    AppDatePicker,
    AppArcSelector,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleSelector,
    AppSimpleTextSearch,
    AppSimpleDatePicker,
    TableCheckList
  }
})
export default class PolicyReports extends mixins(ListPage, RecordTypesHandler, SearchCarrier, SearchCarrierProduct, mixins(SearchProducer, SearchGeneralAgent, SearchBook, ExportExcel)) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Reports'
    },
    {
      text: 'Policy Reports'
    }
  ]
  searchVal = ''
  startDate = ''
  endDate = ''

  complexSortItems = ['expired in last 30 days', 'expire in next 30 days', 'commission report']
  complexSortVals = ['EXPIRED_POLICY', 'EXPIRING_POLICY', 'COMMISSION_REPORT']
  complexSortParams = ['expired_policy', 'expiring_policy', 'commission_report']
  complexSortActive = 2

  policies: Policy[] = []
  policyReports: Policy[] = []
  tableLoading = false

  statisticsLoading = true
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }
  dollarFilter = dollarFilter

  totalProjectedPremium = '0.00'
  totalCommissionReceived = '0.00'
  totalProjectedCommission = '0.00'

  yesOrNo = ['Yes', 'No']

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    policyName: 'POLICY_NAME',
    effectiveDate: 'EFFECTIVE_DATE',
    expirationDate: 'EXPIRATION_DATE',
    number: 'POLICY_NUMBER',
    status: 'POLICY_STATUS'
  }

  tableColumn = [
    { field: 'menu', title: '', slots: { header: 'select_header' }, width: 50, resizable: true },
    {
      field: 'number',
      title: 'Policy Number',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.policyNumber ? row.policyNumber : '-')
          ]
        }
      },
      sortable: true,
      resizable: true,
      width: 150
    },
    {
      field: 'policyName',
      title: 'Policy Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true,
      width: 200
    },
    {
      field: 'status',
      title: 'Status',
      slots: { default: 'status_default' },
      sortable: true,
      resizable: true,
      width: 144
    },
    {
      field: 'type',
      title: 'Type',
      slots: { default: 'type_default' },
      sortable: true,
      resizable: true,
      width: 130
    },
    {
      title: 'Carrier',
      field: 'carrier',
      slots: { default: 'carrier_name_default' },
      resizable: true,
      width: 120
    },
    {
      title: 'Insurance Product',
      field: 'carrierProduct',
      slots: { default: 'product_name_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Source',
      field: 'source',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.source ? row.source.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'Book',
      field: 'book',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.book ? row.book.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'GA',
      field: 'generalAgent',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.generalAgent ? row.generalAgent.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'Effective Date',
      field: 'effectiveDate',
      slots: {
        default: 'effective_default'
      },
      resizable: true,
      sortable: true,
      width: 150
    },
    {
      title: 'Expiration Date',
      field: 'expirationDate',
      slots: {
        default: 'expiration_default'
      },
      sortable: true,
      resizable: true,
      width: 150
    },
    {
      title: 'Bind Date',
      field: 'bindDate',
      slots: {
        default: 'bind_default'
      },
      resizable: true,
      sortable: true,
      width: 120
    },
    {
      title: 'Net Premium',
      field: 'premium',
      slots: { default: 'premium_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Fees',
      field: 'stampingFee',
      slots: { default: 'fees_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Broker Fee',
      field: 'brokerFee',
      slots: { default: 'broker_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Billing Type',
      field: 'billingType',
      slots: { default: 'billing_type_default' },
      resizable: true,
      width: 120
    },
    {
      title: 'Premium Received',
      field: 'totalReceivedPremium',
      slots: { default: 'premium_received_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Commission',
      field: 'totalProjectedPremium',
      slots: { default: 'commission_projected_default' },
      resizable: true,
      width: 110
    },
    {
      title: 'Commission Received',
      field: 'totalReceivedCommission',
      slots: { default: 'commission_received_default' },
      resizable: true,
      width: 170
    },
    {
      title: 'Commission Completed',
      field: 'isCommissionPayOff',
      slots: { default: 'commission_pay_off_default' },
      resizable: true,
      width: 180
    },
    {
      title: 'Cancel Date',
      field: 'cancelDate',
      slots: { default: 'cancel_date_default' },
      resizable: true,
      width: 180
    }
  ]

  policyStatus = policyStatus
  policyStatusClass = policyStatusClass
  policyStatusOptions = statusValues
  policyTypeOptions = policyTypeValues
  billingType = billingType
  policyType = policyType
  policyPaymentOptions = ['> 0', '= 0']

  dateToDisplay = dateToDisplay

  isMounted = false

  generateReportDialog = {
    show: false,
    title: 'Generate Report',
    text: ''
  }

  disableConfirm = false

  isActiveTableMenu = false
  tableCheckList = {
    left: 115,
    top: 177,
    remainingHeight: 196
  }
  tableCheckListShow = false
  selectColumns: any = []
  tableCheckListItems: CheckboxModel = {}

  // 图标列固定宽度:50，其他列固定最小宽度 150，窗口可视区域小于 800 时出现滚动条，防止顶部按钮被挤压，
  get columnWidth () {
    const { selectColumns, windowSize } = this
    const len = selectColumns.length
    const column = { width: 0 }
    if (len > 1) {
      const totalColumnWidth = (len - 1) * 150 + 50
      if (windowSize.w < 800) {
        column.width = Math.floor((800 - 132 - 50) / (len - 1))
      } else {
        column.width = totalColumnWidth > windowSize.w - 132 ? 150 : Math.floor((windowSize.w - 50 - 132) / (len - 1))
      }
    } else {
      // 只有图标列
      column.width = windowSize.w < 800 ? 800 - 132 : (windowSize.w - 132)
    }
    // 设置已选列宽
    const columns = selectColumns.map((item: any) => {
      return {
        ...item,
        width: len <= 1 ? column.width : (item.title ? column.width : 50)
      }
    })
    this.selectColumns = [...columns]
    return column.width
  }

  get tableMinWidth () {
    const { windowSize, columnWidth, selectColumns } = this
    if (windowSize.w < 800) {
      return (800 - 132) + 'px'
    }
    if (selectColumns.length > 1 && ((selectColumns.length - 1) * columnWidth + 50 > windowSize.w - 132)) {
      return columnWidth * (selectColumns.length - 1) + 50 + 'px'
    }
    return (windowSize.w - 132 + 'px')
  }

  get recordTypeOptions () {
    if (this.$R.isEmpty(this.recordTypesOptions)) {
      return []
    }
    return this.recordTypesOptions['POLICY'].map(template => template.title)
  }

  get reportInfoList () {
    if (!this.filter) {
      return []
    } else {
      const filterInfoList: InfoItem[] = []
      if (this.filter.effectiveStartDate || this.filter.effectiveEndDate) {
        if (this.filter.effectiveStartDate && this.filter.effectiveEndDate) {
          const infoItem: InfoItem = {
            title: 'Effective Date',
            value: `${dateToDisplay(+this.filter.effectiveStartDate)} - ${dateToDisplay(+this.filter.effectiveEndDate)}`
          }
          filterInfoList.push(infoItem)
        } else {
          const infoItem: InfoItem = {
            title: 'Effective Date',
            value: `${this.filter.effectiveStartDate ? dateToDisplay(+this.filter.effectiveStartDate) : ''}${this.filter.effectiveEndDate ? dateToDisplay(+this.filter.effectiveEndDate) : ''}`
          }
          filterInfoList.push(infoItem)
        }
      }
      if (this.filter.expirationStartDate || this.filter.expirationEndDate) {
        if (this.filter.expirationStartDate && this.filter.expirationEndDate) {
          const infoItem: InfoItem = {
            title: 'Expiration Date',
            value: `${dateToDisplay(+this.filter.expirationStartDate)} - ${dateToDisplay(+this.filter.expirationEndDate)}`
          }
          filterInfoList.push(infoItem)
        } else {
          const infoItem: InfoItem = {
            title: 'Expiration Date',
            value: `${this.filter.expirationStartDate ? dateToDisplay(+this.filter.expirationStartDate) : ''}${this.filter.expirationEndDate ? dateToDisplay(+this.filter.expirationEndDate) : ''}`
          }
          filterInfoList.push(infoItem)
        }
      }
      if (this.filter.cancelStartDate || this.filter.cancelEndDate) {
        if (this.filter.cancelStartDate && this.filter.cancelEndDate) {
          const infoItem: InfoItem = {
            title: 'Cancel Date',
            value: `${dateToDisplay(+this.filter.cancelStartDate)} - ${dateToDisplay(+this.filter.cancelEndDate)}`
          }
          filterInfoList.push(infoItem)
        } else {
          const infoItem: InfoItem = {
            title: 'Cancel Date',
            value: `${this.filter.cancelStartDate ? dateToDisplay(+this.filter.cancelStartDate) : ''}${this.filter.cancelEndDate ? dateToDisplay(+this.filter.cancelEndDate) : ''}`
          }
          filterInfoList.push(infoItem)
        }
      }
      if (this.filter.ga && this.filter.ga.name) {
        const infoItem: InfoItem = {
          title: 'GA',
          value: this.filter.ga.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.carrier && this.filter.carrier.name) {
        const infoItem: InfoItem = {
          title: 'Carrier',
          value: this.filter.carrier.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.carrierProduct && this.filter.carrierProduct.name) {
        const infoItem: InfoItem = {
          title: 'Insurance Product',
          value: this.filter.carrierProduct.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.recordType !== null && !isNaN(this.filter.recordType) && this.filter.recordType !== -1) {
        const infoItem: InfoItem = {
          title: 'Record type',
          value: this.recordTypes[this.filter.recordType].name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.source && this.filter.source.name) {
        const infoItem: InfoItem = {
          title: 'Source',
          value: this.filter.source.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.book && this.filter.book.name) {
        const infoItem: InfoItem = {
          title: 'Book',
          value: this.filter.book.name
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.policyType !== null && !isNaN(this.filter.policyType) && this.filter.policyType !== -1) {
        const infoItem: InfoItem = {
          title: 'Policy type',
          value: this.policyTypeOptions[this.filter.policyType]
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.expiredDate) {
        const infoItem: InfoItem = {
          title: 'Expiration End Date',
          value: dateToDisplay(+this.filter.expiredDate)
        }
        filterInfoList.push(infoItem)
      }
      if (this.filter.expiringDate) {
        const infoItem: InfoItem = {
          title: 'Expiration Start Date',
          value: dateToDisplay(+this.filter.expiringDate)
        }
        filterInfoList.push(infoItem)
      }

      if (this.filter.hasPayment !== null && !isNaN(this.filter.hasPayment) && this.filter.hasPayment !== -1) {
        const infoItem: InfoItem = {
          title: 'Payment',
          value: this.filter.hasPayment === 0
            ? 'true'
            : 'false'
        }
        filterInfoList.push(infoItem)
      }
      return filterInfoList
    }
  }

  @State(state => state.page) page!: PageState
  @State(state => state.user.me) me!: User

  @Watch('me')
  onMeChanged (val: User) {
    if (val.id) {
      this.updateTableCheckListItems()
    }
  }

  @Watch('tableCheckListItems')
  tableCheckListItemsChanged (val: CheckboxModel) {
    if (!val) {
      return
    }
    const { tableColumn } = this
    const arr = []
    for (let key in val) {
      if (val[key]) {
        const column = R.find((item) => item.title === key, tableColumn)
        arr.push(column)
      }
    }
    this.selectColumns = [tableColumn[0], ...arr]
  }

  @Watch('tableCheckListShow')
  onTableCheckListChanged (val: boolean) {
    this.isActiveTableMenu = val
  }

  @Watch('filter')
  onFilterChanged (val: any) {
    if (val.complexSortActive === 2) {
      this.tableCheckList.top = 246
      this.tableCheckList.remainingHeight = 260
    }
  }

  async created () {
    await this.getRecordTypes('POLICY')
    await this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    this.onResize()
    window.onresize = () => {
      return (() => {
        this.$nextTick(() => {
          this.onResize()
        })
      })()
    }
    await this.restorePage(this.page[this.$route.name || ''])
    if (!this.filter.complexSortActive) {
      this.filter.complexSortActive = 2
    }
    this.checkGetPolicies()
    if (this.me) {
      this.updateTableCheckListItems()
    }
  }

  filterHandler (type?: string) {
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.bindStartDate) {
      filter.bindStartDate = this.filter.bindStartDate.toString()
    }
    if (this.filter.bindEndDate) {
      filter.bindEndDate = this.filter.bindEndDate.toString()
    }
    if (this.filter.effectiveStartDate) {
      filter.effectiveStartDate = this.filter.effectiveStartDate.toString()
    }
    if (this.filter.effectiveEndDate) {
      filter.effectiveEndDate = this.filter.effectiveEndDate.toString()
    }
    if (this.filter.expirationStartDate) {
      filter.expirationStartDate = this.filter.expirationStartDate.toString()
    }
    if (this.filter.expirationEndDate) {
      filter.expirationEndDate = this.filter.expirationEndDate.toString()
    }
    if (this.filter.cancelStartDate) {
      filter.cancelStartDate = this.filter.cancelStartDate.toString()
    }
    if (this.filter.cancelEndDate) {
      filter.cancelEndDate = this.filter.cancelEndDate.toString()
    }
    if (this.filter.carrier && this.filter.carrier.id !== undefined) {
      filter.carrierId = Number(this.filter.carrier.id)
      if (type === 'input') {
        filter.carrierName = this.filter.carrier.name
      }
    }
    if (this.filter.carrierProduct && this.filter.carrierProduct.id !== undefined) {
      filter.carrierProductId = Number(this.filter.carrierProduct.id)
      if (type === 'input') {
        filter.carrierProductName = this.filter.carrierProduct.name
      }
    }
    if (this.filter.source && this.filter.source.id !== undefined) {
      filter.sourceId = Number(this.filter.source.id)
      if (type === 'input') {
        filter.sourceName = this.filter.source.name
      }
    }
    if (this.filter.ga && this.filter.ga.id !== undefined) {
      filter.gaId = Number(this.filter.ga.id)
      if (type === 'input') {
        filter.gaName = this.filter.ga.name
      }
    }
    if (this.filter.book && this.filter.book.id !== undefined) {
      filter.bookId = Number(this.filter.book.id)
      if (type === 'input') {
        filter.bookName = this.filter.book.name
      }
    }
    if (this.filter.recordType !== null && !isNaN(this.filter.recordType) && this.filter.recordType !== -1) {
      filter.recordTypeId = Number(this.recordTypes[this.filter.recordType].id)
      if (type === 'input') {
        filter.recordTypeName = this.recordTypes[this.filter.recordType].name
      }
    }
    if (this.filter.policyType !== null && !isNaN(this.filter.policyType) && this.filter.policyType !== -1) {
      filter.type = policyTypeKeys[this.filter.policyType]
    }
    if (this.filter.complexSortActive === 0) {
      if (this.filter.expiredDate) {
        filter.expirationEndDate = this.filter.expiredDate.toString()
      }
    }
    if (this.filter.complexSortActive === 1) {
      if (this.filter.expiringDate) {
        filter.expirationStartDate = this.filter.expiringDate.toString()
      }
    }

    const nonRenew = this.yesOrNo[this.filter.nonRenew]
    const followup = this.yesOrNo[this.filter.followup]

    filter.nonRenew = nonRenew ? nonRenew === this.yesOrNo[0] : null
    filter.followup = followup ? followup === this.yesOrNo[0] : null
    filter.hasPayment = this.filter.hasPayment === 0
      ? true
      : this.filter.hasPayment === 1
        ? false
        : null

    return filter
  }

  checkGetPolicies () {
    if (this.filter.complexSortActive === undefined || (this.filter.complexSortActive === 2 && !this.filter.bindStartDate && !this.filter.bindEndDate && !this.filter.effectiveStartDate && !this.filter.effectiveEndDate && !this.filter.expirationStartDate && !this.filter.expirationEndDate && !this.filter.cancelStartDate && !this.filter.cancelEndDate && (!this.filter.ga || !this.filter.ga.id) && (!this.filter.carrier || !this.filter.carrier.id) && (!this.filter.carrierProduct || !this.filter.carrierProduct.id) && (!this.filter.source || !this.filter.source.id) && (!this.filter.book || !this.filter.book.id) && (this.filter.recordType === null || isNaN(this.filter.recordType) || this.filter.recordType === -1) && (this.filter.policyType === null || isNaN(this.filter.policyType) || this.filter.policyType === -1) && (this.filter.hasPayment === null || isNaN(this.filter.hasPayment) || this.filter.hasPayment === -1))) {
      this.policies = []
      this.options.tablePage.total = 0
      this.totalProjectedPremium = '0.00'
      this.totalCommissionReceived = '0.00'
      this.totalProjectedCommission = '0.00'
      return
    }
    if (this.filter.complexSortActive === 0) {
      this.options.tablePage.enabled = false
      this.getPolicesExpiredLastMonthList()
    } else if (this.filter.complexSortActive === 1) {
      this.options.tablePage.enabled = false
      this.getPolicesExpiringInAMonthList()
    } else {
      this.options.tablePage.enabled = true
      this.getPolicies()
    }
  }

  onResize () {
    const container = this.$refs.reportscontainer as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  sumFees (item: Policy) {
    const { agencyFee, carrierPolicyFee, inspectionFee, stampingFee, taxesFee } = item
    const sum = +agencyFee + (+carrierPolicyFee) + (+inspectionFee) + (+stampingFee) + (+taxesFee)

    return dollarFilter(sum)
  }

  async getPolicies (pageNumber?: number, perPageSize?: number) {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    const filter = this.filterHandler()
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPoliciesReport,
        variables: {
          filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: pageNumber && perPageSize ? ((pageNumber - 1) * perPageSize) : ((currentPage - 1) * pageSize),
            limit: perPageSize || pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (pageNumber && perPageSize) {
        this.policyReports = this.policyReports.concat(result.data.getPoliciesReport.policies)
      } else {
        this.policies = result.data.getPoliciesReport.policies
        this.totalProjectedPremium = result.data.getPoliciesReport.totalProjectedPremium
        this.totalCommissionReceived = result.data.getPoliciesReport.totalCommissionReceived
        this.totalProjectedCommission = result.data.getPoliciesReport.totalProjectedCommission
      }
      this.isMounted = true
      this.tableLoading = false
      this.options.tablePage.total = result.data.getPoliciesReport.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getPolicesExpiredLastMonthList () {
    const { sortBy, sortDesc } = this.options
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicesExpiredLastMonth,
        variables: {
          expirationEndDate: this.filter.expiredDate ? this.filter.expiredDate.toString() : '',
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.getPolicesExpiredLastMonth
      this.tableLoading = false
      this.options.tablePage.total = result.data.getPolicesExpiredLastMonth.length
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getPolicesExpiringInAMonthList () {
    const { sortBy, sortDesc } = this.options
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicesExpiringInAMonth,
        variables: {
          expirationStartDate: this.filter.expiringDate ? this.filter.expiringDate.toString() : '',
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.getPolicesExpiringInAMonth
      this.tableLoading = false
      this.options.tablePage.total = result.data.getPolicesExpiringInAMonth.length
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    if (!this.filter.complexSortActive) {
      this.filter.complexSortActive = 2
    }
    this.checkGetPolicies()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    if (this.filter.complexSortActive === 0) {
      this.getPolicesExpiredLastMonthList()
    } else if (this.filter.complexSortActive === 1) {
      this.getPolicesExpiringInAMonthList()
    } else {
      this.getPolicies()
    }
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getPolicies()
  }

  routeToDetail (item: any) {
    return {
      name: 'PolicyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    return {
      name: 'CarrierDetailDetail',
      params: {
        id: item.carrier.id
      }
    }
  }

  routeToCarrierProductDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.carrierProduct.id
      }
    }
  }

  generateHandler () {
    this.generateReportDialog.text = ''
    if (this.filter.complexSortActive === 0) {
      if (this.filter.expiredDate) {
        this.generateReportDialog.text = `Report Start Date: ${dateToDisplay(+this.filter.expiredDate)}`
      } else {
        this.generateReportDialog.text = 'expired in last 30 days'
      }
    }
    if (this.filter.complexSortActive === 1) {
      if (this.filter.expiringDate) {
        this.generateReportDialog.text = `Report Start Date: ${dateToDisplay(+this.filter.expiringDate)}`
      } else {
        this.generateReportDialog.text = 'expire in next 30 days'
      }
    }
    if (this.filter.complexSortActive === 2) {
      if (this.reportInfoList.length > 0) {
        for (var i = 0; i < this.reportInfoList.length; i++) {
          this.generateReportDialog.text += `${this.reportInfoList[i].title}:${this.reportInfoList[i].value}; `
        }
      } else {
        this.generateReportDialog.text = 'commission report'
      }
    }
    this.generateReportDialog.show = true
  }

  async generateConfirm () {
    const input = this.filterHandler('input')
    const { selectColumns } = this
    const displayColumns = R.filter((item: any) => item.title, selectColumns).map((item) => item.title)
    if (!displayColumns.length) {
      return
    }
    try {
      const res = await this.$apollo.mutate({
        mutation: exportPolicyReports,
        variables: {
          exportType: this.complexSortVals[+this.filter.complexSortActive],
          input,
          displayColumns
        }
      })
      if (res.data.exportPolicyReports) {
        showNotification({
          msg: 'Generate report successfully',
          type: SnackType.Success
        })
      }
      this.generateReportDialog.show = false
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  setTableCheckListItems () {
    const { tableColumn } = this
    const columns = R.filter((item) => item.field !== 'menu', tableColumn)
    const columnsObj = R.groupBy((item) => item.title, columns)
    const tableCheckListItems: CheckboxModel = {}
    for (let key in columnsObj) {
      tableCheckListItems[key] = true
    }
    return tableCheckListItems
  }

  showTableCheckList () {
    this.tableCheckListShow = true
  }

  onMenuChecklistChange () {
    this.updateTableCheckListItems()
  }

  updateTableCheckListItems () {
    const key = 'VERGE_WF_POLICY_REPORT_TABLE_COLUMNS_' + this.me.id
    const itemsStr = localStorage.getItem(key)
    const tableCheckListItems = itemsStr ? JSON.parse(itemsStr) : this.setTableCheckListItems()
    this.tableCheckListItems = tableCheckListItems
  }
}
